import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Accessibility, CheckCircle2, AlertTriangle, ChevronRight, Loader2, Upload, Eye } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Status colors
const STATUS_COLORS = {
  accessible: '#059669',
  issues_found: '#d97706',
  unknown: '#6b7280',
  no_data: '#6b7280',
};

function AccessibilityWidget() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const storedSession = localStorage.getItem('eyvo_session');
        const response = await fetch(`${API_URL}/api/accessibility/summary`, {
          credentials: 'include',
          headers: storedSession ? { 'Authorization': `Bearer ${storedSession}` } : {},
        });
        if (response.ok) {
          const data = await response.json();
          setSummary(data);
        }
      } catch (error) {
        console.error('Failed to fetch accessibility summary:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, []);

  if (loading) {
    return (
      <div className="eyvo-card accessibility-widget loading">
        <Loader2 className="spin" size={20} />
      </div>
    );
  }

  if (!summary || summary.total_reports === 0) {
    return (
      <div className="eyvo-card accessibility-widget empty" data-testid="accessibility-widget">
        <div className="empty-state-mini">
          <div className="empty-icon">
            <Accessibility size={32} color="#6b7280" />
          </div>
          <div className="empty-text">
            <h4>No Accessibility Audits</h4>
            <p>Upload WCAG compliance scan reports to track accessibility status.</p>
          </div>
          <Link to="/accessibility" className="btn-primary" data-testid="upload-a11y-btn">
            <Upload size={16} /> Upload Report
          </Link>
        </div>
      </div>
    );
  }

  const { current_status, latest_results, latest_scan, target_url, scanner } = summary;
  const scorePct = latest_results?.score_pct || 0;

  return (
    <div className="eyvo-card accessibility-widget" data-testid="accessibility-widget">
      {/* Stats Row */}
      <div className="a11y-stats">
        <div className="stat-item">
          {current_status === 'accessible' ? (
            <CheckCircle2 size={20} color={STATUS_COLORS.accessible} />
          ) : current_status === 'issues_found' ? (
            <AlertTriangle size={20} color={STATUS_COLORS.issues_found} />
          ) : (
            <Accessibility size={20} color={STATUS_COLORS.unknown} />
          )}
          <div className="stat-content">
            <span className="stat-value" style={{ color: STATUS_COLORS[current_status] }}>
              {current_status === 'accessible' ? 'Accessible' : 
               current_status === 'issues_found' ? 'Issues' : 'Unknown'}
            </span>
            <span className="stat-label">Status</span>
          </div>
        </div>
        <div className="stat-item">
          <Eye size={20} color="#2563eb" />
          <div className="stat-content">
            <span className="stat-value">{scorePct}%</span>
            <span className="stat-label">Score</span>
          </div>
        </div>
        <div className="stat-item">
          <CheckCircle2 size={20} color="#059669" />
          <div className="stat-content">
            <span className="stat-value">{latest_results?.passed || 0}</span>
            <span className="stat-label">Passed</span>
          </div>
        </div>
        <div className="stat-item">
          <AlertTriangle size={20} color="#dc2626" />
          <div className="stat-content">
            <span className="stat-value">{latest_results?.failed || 0}</span>
            <span className="stat-label">Failed</span>
          </div>
        </div>
      </div>

      {/* Scan Info */}
      <div className="a11y-scan-info">
        <div className="scan-row">
          <span className="scan-label">Last Scan:</span>
          <span className="scan-value">{latest_scan || 'Unknown'}</span>
        </div>
        <div className="scan-row">
          <span className="scan-label">Scanner:</span>
          <span className="scan-value">{scanner || 'Unknown'}</span>
        </div>
        {target_url && (
          <div className="scan-row">
            <span className="scan-label">Target:</span>
            <span className="scan-value">{target_url}</span>
          </div>
        )}
      </div>

      {/* Link to full page */}
      <Link to="/accessibility" className="widget-link">
        View All Accessibility Audits <ChevronRight size={14} />
      </Link>
    </div>
  );
}

export default AccessibilityWidget;
